import { observer } from 'mobx-react'
import React, { ReactElement, useMemo, useState } from 'react'

import Modal, { ModalProps } from '../../components/Modal'
import { RADOM_COLORS } from '../../util/Constants'
import { errorToast, successToast, useTestMode } from '../../util/Util'
import { DepositMethods } from '../../util/Managed'
import { InputLabel, TextInputWithLabel } from '../../components/Input'
import { PrimaryButton } from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import Dropdown, { DropdownItem } from '../../components/Dropdown'

interface Leaf { network: string, token?: string, name: string, logo: string, ticker?: string }

interface Selected { network: string, token: string | undefined }

interface ICreateDepositAddressModal extends ModalProps {
  onAccountCreated: () => void
  customerId: string
}

const NATIVE = 'NATIVE'

const CreateDepositAddresCustomerModal = observer(({
  title,
  onAccountCreated,
  onClose,
  visible,
  customerId
}: ICreateDepositAddressModal): ReactElement => {
  const [accountName, setAccountName] = useState('')
  const [selectedDepositMethods, setSelectedDepositMethods] =
    useState<Selected[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [testMode] = useTestMode()

  const createAddress = async (): Promise<void> => {
    if (selectedDepositMethods.length <= 0) {
      return
    }
    setIsLoading(true)
    try {
    //   await Radom.createDepositAccount({
    //     name: accountName,
    //     accountDetails: {
    //       Crypto: {
    //         depositMethods: selectedDepositMethods
    //           .map(m => ({
    //             network: m.network,
    //             token: m.token
    //           })),
    //         isExternal: true,
    //         customerId
    //       }
    //     }
    //   })
      onAccountCreated()
      successToast('Deposit address created')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const leafKey = (x: Selected | Leaf): string => `${x.network}:${x.token ?? NATIVE}`

  const collectLeaves = (node: any): Leaf[] =>
    node?.method
      ? [{
        network: node.method.network,
        token: node.method.token,
        name: node.name,
        logo: node.logo,
        ticker: node.ticker
      }]
      : (node?.subMethods ?? []).flatMap(collectLeaves)

  const getCheckState = (leaves: Leaf[], selectedSet: Set<string>): { all: boolean, partial: boolean } => {
    const total = leaves.length
    const sel = leaves.reduce((a, l) => a + (selectedSet.has(leafKey(l as Selected)) ? 1 : 0), 0)
    const all = total > 0 && sel === total
    const none = sel === 0
    return { all, partial: !none && !all }
  }

  const addAll = (selected: Selected[], leaves: Leaf[]): Selected[] => {
    const s = new Set(selected.map(leafKey))
    leaves.forEach(l => s.add(leafKey(l as Selected)))
    return Array.from(s).map(k => {
      const [network, tok] = k.split(':')
      return { network, token: tok === NATIVE ? undefined : tok }
    })
  }

  const removeAll = (selected: Selected[], leaves: Leaf[]): Selected[] => {
    const r = new Set(leaves.map(l => leafKey(l as Selected)))
    return selected.filter(x => !r.has(leafKey(x)))
  }

  const toggleLeaf = (selected: Selected[], leaf: Leaf): Selected[] => {
    const k = leafKey(leaf as Selected)
    const exists = selected.some(s => leafKey(s) === k)
    return exists ? selected.filter(s => leafKey(s) !== k) : [...selected, { network: leaf.network, token: leaf.token }]
  }

  const topGroups = useMemo(
    () => DepositMethods.filter(d => (testMode ? d.isTestnet : !d.isTestnet)),
    [testMode]
  )
  const selectedSet = useMemo(() => new Set(selectedDepositMethods.map(leafKey)), [selectedDepositMethods])
  const selectedLeafCount = useMemo(() => selectedDepositMethods.length, [selectedDepositMethods])

  return <Modal
    title={title}
    visible={visible}
    onClose={onClose}
    innerStyles={{ width: 550 }}>

    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'start',
      gap: 10,
      padding: '0px 30px'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        <TextInputWithLabel
          label='Account nickname'
          placeholder='Enter a name for this account'
          disabled={isLoading}
          required
          value={accountName}
          onChange={(e) => {
            const el = (e.target as HTMLInputElement)
            const value = el.value
            setAccountName(value)
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <InputLabel>Deposit methods</InputLabel>
          <Dropdown
            innerStyle={{ maxHeight: 300 }}
            disabled={isLoading}
            selectedContent={
              selectedLeafCount > 0
                ? <span style={{ fontSize: 14 }}>{selectedLeafCount} methods selected</span>
                : <span style={{ fontSize: 14 }}>Select deposit methods</span>
            }
            dropdownContent={
              <div onMouseDown={(e) => e.stopPropagation()}>
                {topGroups.map((group, idx) => {
                  const groupLeaves = collectLeaves(group)
                  const groupState = getCheckState(groupLeaves, selectedSet)

                  return (
                    <div key={`g-${idx}`}>
                      <DropdownItem
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedDepositMethods(prev => groupState.all
                            ? removeAll(prev, groupLeaves)
                            : addAll(prev, groupLeaves))
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Checkbox checked={groupState.all} />
                          <div
                            style={{
                              height: 25,
                              width: 25,
                              backgroundImage: `url(${group.logo})`,
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: 'contain',
                              backgroundPosition: 'center',
                              position: 'relative'
                            }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: 14 }}>{group.name}</span>
                            {group.description &&
                            <span style={{ fontSize: 12, opacity: 0.5 }}>{group.description}</span>}
                          </div>
                        </div>
                      </DropdownItem>

                      {(group.subMethods ?? []).map((net: any, jdx: number) => {
                        const netLeaves = collectLeaves(net)
                        const netState = getCheckState(netLeaves, selectedSet)
                        const isLeafNode = !!net?.method

                        if (isLeafNode) {
                          const leaf = netLeaves[0]
                          const leafState = getCheckState([leaf], selectedSet)
                          return (
                            <div key={`l-${idx}-${jdx}`}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 45 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, height: '100%' }}>
                                  <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                  <div style={{ width: 10, height: 1, backgroundColor: RADOM_COLORS.GRAY8 }} />
                                </div>
                                <DropdownItem
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setSelectedDepositMethods(prev => toggleLeaf(prev, leaf))
                                  }}
                                  style={{ width: '100%' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <Checkbox checked={leafState.all} />
                                    <div
                                      style={{
                                        height: 25,
                                        width: 25,
                                        backgroundImage: `url(${leaf.logo})`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: 'contain',
                                        backgroundPosition: 'center',
                                        position: 'relative'
                                      }} />
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <span style={{ fontSize: 14 }}>{leaf.name}</span>
                                      {leaf.ticker && <span style={{ fontSize: 12, opacity: 0.5 }}>{leaf.ticker}</span>}
                                    </div>
                                  </div>
                                </DropdownItem>
                              </div>
                            </div>
                          )
                        }

                        return (
                          <div key={`n-${idx}-${jdx}`}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 45 }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, height: '100%' }}>
                                <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                <div style={{ width: 10, height: 1, backgroundColor: RADOM_COLORS.GRAY8 }} />
                              </div>
                              <DropdownItem
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSelectedDepositMethods(prev =>
                                    netState.all
                                      ? removeAll(prev, netLeaves)
                                      : addAll(prev, netLeaves))
                                }}
                                style={{ width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <Checkbox checked={netState.all} />
                                  <div
                                    style={{
                                      height: 25,
                                      width: 25,
                                      backgroundImage: `url(${net.logo})`,
                                      backgroundRepeat: 'no-repeat',
                                      backgroundSize: 'contain',
                                      backgroundPosition: 'center',
                                      position: 'relative'
                                    }} />
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: 14 }}>{net.name}</span>
                                    {net.ticker && <span style={{ fontSize: 12, opacity: 0.5 }}>{net.ticker}</span>}
                                  </div>
                                </div>
                              </DropdownItem>
                            </div>

                            {(net.subMethods ?? []).map((tok: any, kdx: number) => {
                              const tokenLeaves = collectLeaves(tok)
                              const leaf = tokenLeaves[0]
                              const leafState = getCheckState(tokenLeaves, selectedSet)

                              return (
                                <div key={`t-${idx}-${jdx}-${kdx}`} style={{ display: 'flex', alignItems: 'center', gap: 4, height: 48 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, height: '100%' }}>
                                    <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', marginLeft: 25, height: '100%' }}>
                                    <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                    <div style={{ width: 10, height: 1, backgroundColor: RADOM_COLORS.GRAY8 }} />
                                  </div>

                                  <DropdownItem
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setSelectedDepositMethods(prev => toggleLeaf(prev, leaf))
                                    }}
                                    style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                      <Checkbox checked={leafState.all} />
                                      <div
                                        style={{
                                          height: 25,
                                          width: 25,
                                          backgroundImage: `url(${tok.logo})`,
                                          backgroundRepeat: 'no-repeat',
                                          backgroundSize: 'contain',
                                          backgroundPosition: 'center',
                                          position: 'relative'
                                        }} />
                                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontSize: 14 }}>{tok.name}</span>
                                        {tok.ticker && <span style={{ fontSize: 12, opacity: 0.5 }}>{tok.ticker}</span>}
                                      </div>
                                    </div>
                                  </DropdownItem>
                                </div>
                              )
                            })}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            }
          />
        </div>

      </div>
    </div>

    <div style={{
      padding: 30,
      borderRadius: 15,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0
    }}>
      <PrimaryButton
        isLoading={isLoading}
        disabled={!accountName || selectedDepositMethods.length <= 0 }
        style={{ width: '100%' }}
        onClick={() => {
          createAddress()
        }}>
          Create deposit account
      </PrimaryButton>
    </div>

  </Modal>
})

export default CreateDepositAddresCustomerModal
